import type { Allocation } from '@moment/horizon-data-access/proposal'
import type { TableColumn } from '@/components/atomic/table-basic'

import { ProposalStatus } from '@moment/horizon-data-access/proposal'
import { toError } from '@moment/observability/logger'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Icon, { IconName } from '@/components/atomic/icon'
import Input from '@/components/atomic/input'
import { BodySubtext } from '@/components/atomic/text'
import { createLogger } from '@/lib/logger'
import {
  convertNumBondsToParValue,
  convertParValueToNumBonds,
  formatQuantity,
} from '@/utils/presentation/bonds'
import { formatMoney } from '@/utils/presentation/formatting'
import { cn } from '@/utils/shadcn/utils'

import { useQuantityInput } from './helpers'

interface ColumnProps {
  onUpdateAllocation: (allocationId: string, quantity: number) => void
  onDeleteClick: (allocation: Allocation) => void
  selectedAccount?: string
  proposalStatus?: ProposalStatus
  accountNameMap: Record<string, string>
  disableQuantityEditing?: boolean
  hideCancelAllocation?: boolean
}

const logger = createLogger('ProposalAllocationTable')

// helper to determine if a row is selectable based on account
function isRowSelectable(
  rowAccountId: string,
  selectedAccount?: string,
): boolean {
  return selectedAccount ? rowAccountId === selectedAccount : true
}

// conditional text styling for disabled state
function getDisabledTextStyle(
  rowAccountId: string,
  selectedAccount?: string,
): string {
  return cn(
    selectedAccount &&
      rowAccountId !== selectedAccount &&
      'text-moment-blue-grey-400',
  )
}

// quantity input cell component with error handling
function QuantityCell({
  row,
  onUpdateAllocation,
  isEditable,
  proposalStatus,
  disableQuantityEditing,
}: {
  row: Allocation
  onUpdateAllocation: (id: string, quantity: number) => void
  isEditable: boolean
  proposalStatus?: ProposalStatus
  disableQuantityEditing?: boolean
}) {
  const { inputQuantity, handleQuantityChange, handleQuantityUpdate } =
    useQuantityInput({
      initialQuantity: convertParValueToNumBonds(row.quantity), // The API provides the quantity in par value, so we need to convert it to number of bonds for the form
      onUpdate: (newQuantity) => {
        try {
          // The helper now guarantees newQuantity is valid and positive
          onUpdateAllocation(
            row.proposal_allocation_id,
            convertNumBondsToParValue(newQuantity), // The form uses number of bonds, so we need to convert it to par value for the API
          )
        } catch (error) {
          logger.error('Failed to update allocation quantity:', {
            error: toError(error),
            allocationId: row.proposal_allocation_id,
            quantity: newQuantity,
            accountId: row.moment_account_id,
          })
        }
      },
    })

  // Format the display value based on inputQuantity state
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  const displayValue = !isNaN(inputQuantity)
    ? inputQuantity.toString()
    : formatQuantity(row.quantity)

  return (
    <>
      {proposalStatus === ProposalStatus.executing ||
      proposalStatus === ProposalStatus.completed ? (
        <BodySubtext>
          {formatQuantity(row.filled_quantity)} / {formatQuantity(row.quantity)}
        </BodySubtext>
      ) : (
        <Input
          value={displayValue}
          size="small"
          placeholder={formatQuantity(row.quantity)}
          onChange={(e) => handleQuantityChange(e.target.value)}
          onBlur={handleQuantityUpdate}
          disabled={!isEditable || disableQuantityEditing}
          className={cn(
            (!isEditable || disableQuantityEditing) &&
              'text-moment-blue-grey-400',
          )}
        />
      )}
    </>
  )
}

export function getColumns({
  onUpdateAllocation,
  onDeleteClick,
  selectedAccount,
  proposalStatus,
  accountNameMap,
  disableQuantityEditing,
  hideCancelAllocation = false,
}: ColumnProps): TableColumn<Allocation>[] {
  // base columns that are always present
  const baseColumns: TableColumn<Allocation>[] = [
    {
      key: 'moment_account_id',
      header: 'Account name',
      cellRenderer: (row) => (
        <BodySubtext
          className={getDisabledTextStyle(
            row.moment_account_id,
            selectedAccount,
          )}
        >
          {accountNameMap[row.moment_account_id] || row.moment_account_id}
        </BodySubtext>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      cellRenderer: (row) => (
        <BodySubtext
          className={getDisabledTextStyle(
            row.moment_account_id,
            selectedAccount,
          )}
        >
          {formatMoney(row.market_value, { numDigits: 0 })}
        </BodySubtext>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      cellRenderer: (row) => (
        <div
          className={cn(
            'w-full',
            getDisabledTextStyle(row.moment_account_id, selectedAccount),
          )}
        >
          <QuantityCell
            key={`${row.proposal_allocation_id}-quantity`}
            row={row}
            onUpdateAllocation={onUpdateAllocation}
            isEditable={isRowSelectable(row.moment_account_id, selectedAccount)}
            proposalStatus={proposalStatus}
            disableQuantityEditing={disableQuantityEditing}
          />
        </div>
      ),
    },
  ]

  // conditionally add delete column when no account is selected
  if (
    !selectedAccount &&
    proposalStatus !== ProposalStatus.executing &&
    proposalStatus !== ProposalStatus.completed &&
    !hideCancelAllocation
  ) {
    baseColumns.push({
      key: 'cancel_allocation',
      header: 'Cancel allocation',
      align: 'right',
      cellRenderer: (row) => (
        <Button
          key={`${row.proposal_allocation_id}-delete`}
          className="cursor-pointer"
          icon={
            <Icon
              iconName={IconName.MinusCircleIcon}
              className="text-moment-red-700 dark:text-moment-red-400"
              outline
            />
          }
          variant={ButtonVariant.Secondary}
          onClick={() => onDeleteClick(row)}
        />
      ),
    })
  }

  return baseColumns
}
